import {
	Links,
	TableQueryFilter,
	ZomentumPSA,
	ArchievedWithSyncModel
} from "../common";
import { ProductThumbnailSize } from "../ProposalBuilder/Block";
import {
	Integrations,
	CustomField,
	AutoPriceUpdateDetails,
	Supplier,
	CustomDistributorIntegration
} from "../Settings";
import { IOptionProps } from "@atoms/ZSelect/interface";
import { SKUPricingPlanFormValues } from "../index";

export class ProductCount {
	bundle = 0;
	hardware = 0;
	service = 0;
	software = 0;
	all = 0;
}

export enum ProductType {
	Hardware = "hardware",
	Software = "software",
	Service = "service",
	Bundle = "bundle",
	Labor = "labor",
	Other = "other"
}

export enum EditOrCloneProductDrawerMode {
	EDIT = "edit",
	CLONE = "clone"
}

export const ProductTypes = [
	ProductType.Hardware,
	ProductType.Software,
	ProductType.Service,
	ProductType.Bundle,
	ProductType.Labor
];

export const ProductTypeObj = {
	[ProductType.Hardware]: "hardware",
	[ProductType.Software]: "software",
	[ProductType.Service]: "service",
	[ProductType.Bundle]: "bundle",
	[ProductType.Labor]: "labor",
	[ProductType.Other]: "other"
};

export const ProductTypeNames = {
	[ProductType.Hardware]: "Hardware",
	[ProductType.Software]: "Software",
	[ProductType.Service]: "Service",
	[ProductType.Bundle]: "Bundle",
	[ProductType.Labor]: "Labor",
	[ProductType.Other]: "Other"
};

export enum ProductBillingType {
	OneTime = "one_time",
	Weekly = "weekly",
	Monthly = "monthly",
	Annual = "annual",
	Quarterly = "quarterly",
	SemiAnnually = "semi_annually"
}

export const ProductBillingTypeDisplayText = {
	[ProductBillingType.OneTime]: "One Time",
	[ProductBillingType.Weekly]: "Weekly",
	[ProductBillingType.Monthly]: "Monthly",
	[ProductBillingType.Annual]: "Annually",
	[ProductBillingType.Quarterly]: "Quarterly",
	[ProductBillingType.SemiAnnually]: "Semi Annually"
};

export const billingPeriodUnitText: Record<ProductBillingType, string> = {
	[ProductBillingType.Weekly]: "week",
	[ProductBillingType.Monthly]: "month",
	[ProductBillingType.Quarterly]: "quarter",
	[ProductBillingType.SemiAnnually]: "semi-annually",
	[ProductBillingType.Annual]: "year",
	[ProductBillingType.OneTime]: "(one time)"
};

export enum ProductTaxType {
	default = "default",
	disabled = "disabled"
}

export const productTaxTypeOptions = {
	[ProductTaxType.default]: "Taxable",
	[ProductTaxType.disabled]: "Non Taxable"
};

export const productBillingTypeOptions: Record<ProductBillingType, string> = {
	[ProductBillingType.OneTime]: "One Time",
	[ProductBillingType.Weekly]: "Weekly",
	[ProductBillingType.Monthly]: "Monthly",
	[ProductBillingType.Annual]: "Annually",
	[ProductBillingType.Quarterly]: "Quarterly",
	[ProductBillingType.SemiAnnually]: "Semi-Annually"
};

export type BundleProduct = {
	item_id: string;
	sku_id?: string;
	item: Product;
	quantity: number;
	name?: string | null;
	description?: string | null;
	is_setup_fee_linked_to_quantity?: boolean;
	product_source?: ProductSource | null;
	selected_pricing_id?: string | null;
	selected_setup_price?: number | null;
	selected_cost_price?: number | null;
	selected_billing_period?: ProductBillingType | null;
	supplier_id?: string | null;
	supplier?: Supplier | null;
	is_optional?: boolean | null;
	is_selected?: boolean | null;
	sell_price?: number | null;
	should_add_setup_price_to_margin?: boolean | null;
	tax_category_id?: string;
	discount?: number | null;
	custom_fields?: CustomField[];
	type?: ProductType | null;
	product_thumbnail_size?: ProductThumbnailSize;
	mandatory_custom_field_check_require: boolean;
	vendor_sku_no?: string;
};

export type AddOrEditBundleRequest = Omit<BundleProduct, "item"> & {
	item?: Product;
};

export type ProductTemplate = { name: string; id: string };

export class Product extends ArchievedWithSyncModel {
	bundled_items?: BundleProduct[] = new Array<BundleProduct>();
	created: Date = new Date();
	description = "";
	id = "";
	name = "";
	partner_id = "";
	pricing: ProductPricingPlan[] = new Array<ProductPricingPlan>();
	product_category?: string = "";
	product_source?: ProductSource;
	type: ProductType | null = null;
	logo = "";
	item_images: string[] = new Array<string>();
	updated: Date = new Date();
	vendor_id = "";
	manufacturer?: string;
	tax_category_id?: string;
	manufacturer_part_no?: string;
	custom_fields?: CustomField[];
	show_individual_product_pricing?: boolean;
	should_add_setup_price_to_margin?: boolean;
	read_only?: boolean;
	etilize_skus_info?: [];
	icecat_skus_info?: [];
	unit_of_measure?: string;
	is_duplicate_manufacturer_part_no = false;
	templates?: ProductTemplate[];
	mandatory_custom_field_check_require = false;
	selected_pricing_distributor?: Distributor | null = null;
	auto_price_update_details?: AutoPriceUpdateDetails;
	vendor_company_id?: string | null = null;
	vendor_name?: string | null = null;
	vendor_sku_no?: string | null = null;
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	show_child_items_in_quote_block?: boolean;
	show_child_quantity?: boolean;
	asin?: string;
	identifier?: string;
}

export interface SelectedCustomDistributorCustomField {
	readonly id: string;
	readonly values: string[];
}
export interface SelectedCustomDistributorDetails {
	custom_distributor_id: string;
	selected_custom_distributor_pricing_id: string;
	custom_fields: SelectedCustomDistributorCustomField[];
}

export class ProductsRequest {
	filters: TableQueryFilter[];
	keywords: string | null;
	item_source: ProductSource | null = null;
	cursor: string | null = null;
	constructor(
		filters: TableQueryFilter[],
		keywords: string | null,
		item_source: ProductSource | null,
		cursor: string | null
	) {
		this.filters = filters;
		this.keywords = keywords;
		this.item_source = item_source;
		this.cursor = cursor;
	}
}

export class ProductTemplates {
	template_ids: string[];
	item_id: string;

	constructor(template_ids: string[], item_id: string) {
		this.template_ids = template_ids;
		this.item_id = item_id;
	}
}

export class ProductsResponse {
	base = "";
	filtered_count_map: ProductCount = new ProductCount();
	data: Product[] = new Array<Product>();
	links: Links = new Links();
	next = "";
}

export class QuoteBlockProductPricingRequest {
	product_id = "";
	pricing_id = "";
	sku = "";
	mfg_no = "";
}

export interface CurrencyExchangeDetails {
	from: string;
	to: string;
	rate: number;
	original: Record<string, number>;
}

export class ProductPricingPlan {
	created: Date = new Date();
	id: string | null = null;
	item_id: string | null = null;
	session_id: string | null = null;
	name = "";
	description = "";
	billing_period: ProductBillingType = ProductBillingType.OneTime;
	cost_price = 0;
	sell_price = 0;
	setup_price = 0;
	updated: Date = new Date();
	distributor_pricing_details: DistributorPricingDetails | null = null;
	markup = 0;
	margin = 0;
	vendor_name?: string;
	vendor_sku_no?: string;
	currency_exchange_details: CurrencyExchangeDetails | null = null;
	supplier_id?: string | null;
	supplier?: Supplier | null;
	amazon_business_pricing_details?: AmazonBusinessPricingDetail;
	manufacturer?: string;

	public static fromSKUPricingPlan = (
		skuPricing: SKUPricingPlanFormValues
	): ProductPricingPlan => {
		const pricingPlan = new ProductPricingPlan();
		pricingPlan.name = skuPricing.name;
		pricingPlan.description = skuPricing.description;
		pricingPlan.billing_period = skuPricing.billing_period;
		pricingPlan.cost_price = skuPricing.cost_price;
		pricingPlan.sell_price = skuPricing.sell_price;
		pricingPlan.markup = skuPricing.markup ?? 0;
		pricingPlan.margin = skuPricing.margin ?? 0;
		pricingPlan.setup_price = skuPricing.setup_price;
		pricingPlan.vendor_sku_no = skuPricing.vendor_sku_no;
		return pricingPlan;
	};
}

export interface AmazonBusinessPricingDetail {
	readonly billing_period: string;
	readonly cost_price: number;
	readonly delivery_information: string;
	readonly is_prime: boolean;
	readonly sell_price: number;
	readonly shipping_cost: number;
	readonly product_condition: string;
	readonly tax_exclusive_price: TaxExclusivePrice;
}

export interface TaxExclusivePrice {
	readonly label: string;
	readonly price: number;
}

export enum Distributor {
	Amazon = "amazon_business",
	Ingram = "ingram",
	TechData = "tech_data",
	DAndH = "d_and_h",
	Synnex = "synnex",
	DickerData = "dicker_data",
	Westcoast = "westcoast",
	CustomDistributor = "custom_distributor"
}

export const distributors: string[] = [
	Distributor.Ingram,
	Distributor.TechData,
	Distributor.DAndH,
	Distributor.Synnex,
	Distributor.DickerData,
	Distributor.Westcoast,
	Distributor.CustomDistributor
];

export enum PricingOptions {
	Catalog = "catalog",
	Custom = "custom"
}

export type PricingRadioOptions = Distributor | PricingOptions;

export const distributorLabel: Record<Distributor, string> = {
	[Distributor.Ingram]: "Ingram",
	[Distributor.TechData]: "Tech Data",
	[Distributor.DAndH]: "D&H",
	[Distributor.Synnex]: "Synnex",
	[Distributor.DickerData]: "Dicker Data",
	[Distributor.Westcoast]: "Westcoast",
	[Distributor.CustomDistributor]: "Custom Distributor",
	[Distributor.Amazon]: "Amazon Business"
};

export const ratailPriceLabel: Record<Distributor, string> = {
	[Distributor.Ingram]: "Retail Price",
	[Distributor.TechData]: "Retail Price",
	[Distributor.DAndH]: "Retail Price",
	[Distributor.Synnex]: "Retail Price",
	[Distributor.DickerData]: "Retail Price",
	[Distributor.Westcoast]: "Retail Price",
	[Distributor.CustomDistributor]: "Retail Price",
	[Distributor.Amazon]: "Amazon Business"
};

export interface RetailPriceInfo {
	vendor_name: string;
	distributor: Distributor;
	price: string | number;
}

export const noOfDistributorsIntegrated = (integrations: Integrations) => {
	let count = 0;
	count += integrations.ingram !== null ? 1 : 0;
	count += integrations.tech_data !== null ? 1 : 0;
	count += integrations.d_and_h !== null ? 1 : 0;
	count += integrations.synnex !== null ? 1 : 0;
	count += integrations.dicker_data !== null ? 1 : 0;
	count += integrations.westcoast !== null ? 1 : 0;
	count += Array.isArray(integrations.custom_distributor)
		? integrations?.custom_distributor?.length
		: 0;
	return count;
};

export const displayPricingName = (pricing: ProductPricingPlan | null) => {
	const distributor = pricing?.distributor_pricing_details?.distributor;
	if (distributor === Distributor.CustomDistributor) {
		return pricing?.vendor_name || pricing?.name || "";
	}
	const name = pricing?.distributor_pricing_details?.distributor
		? distributorLabel[pricing.distributor_pricing_details.distributor] ||
		  pricing.distributor_pricing_details.distributor
		: pricing?.name || "";

	return name;
};

export class DistributorPricingDetails {
	distributor: Distributor = Distributor.Ingram;
	total_availability = 0;
	total_on_order = 0;
	billing_period: ProductBillingType | null = null;
	sell_price = 0;
	setup_price = 0;
	cost_price = 0;
	branch_wise_details: Array<DistributorBranchDetails> = [];
	vendor_name = "";
	vendor_sku_number?: number;
	retail_price?: null | number = null;
	custom_distributor_pricing_details?: customDistributorPricingDetails;
}

export interface customDistributorPricingDetails {
	custom_distributor: CustomDistributorIntegration;
	custom_distributor_pricing_id: string;
	custom_fields: SelectedCustomDistributorCustomField[];
}

export class DistributorBranchDetails {
	branch_name = "";
	availability = 0;
	on_order = 0;
	eta_date: Date = new Date();
}

export interface APISyncMetaData {
	sync_source: string;
	sync_source_id: string;
}

export class AddOrEditProductRequest {
	bundled_items?: AddOrEditBundleRequest[] =
		new Array<AddOrEditBundleRequest>();
	description = "";
	id?: string;
	name = "";
	pricing: ProductPricingPlan[] = new Array<ProductPricingPlan>();
	product_category?: string;
	type: ProductType | null = null;
	item_images: string[] = new Array<string>();
	vendor_id = "";
	manufacturer?: string;
	tax_category_id?: string;
	manufacturer_part_no?: string;
	custom_fields?: CustomField[];
	show_individual_product_pricing?: boolean;
	should_add_setup_price_to_margin?: boolean;
	unit_of_measure?: string;
	should_update_templates?: boolean = false;
	mandatory_custom_field_check_require = false;
	selected_pricing_id?: string;
	api_sync_metadata?: APISyncMetaData;
	selected_pricing_distributor?: PricingRadioOptions | null = null;
	supplier_id?: string | null;
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	show_child_items_in_quote_block?: boolean;
	show_child_quantity?: boolean;
	asin?: string;
}
export class ProductFormValues extends Product {
	custom_field_form_values: Record<string, string> = {};
	billing_period?: ProductBillingType;
	selected_billing_period?: ProductBillingType;
	selected_cost_price?: number;
	sell_price?: number;
	selected_setup_price?: number;
	markup?: number;
	margin?: number;
	unit_of_measure?: string;
	vendor_name?: string;
	supplier_id?: string | null;
	vendor_sku_no?: string;
	update_existing_templates?: boolean;
}

export enum ProductSource {
	All = "self,etilize",
	Self = "self",
	Etilize = "etilize",
	DellQuote = "dell_quote",
	ZomentumMarketplace = "marketplace",
	Icecat = "icecat",
	Amazon = "amazon_business",
	CustomDistributor = "custom_distributor",
	DickerData = "dicker_data"
}

export enum SelfCatalogFilterKeys {
	FilterByProductType = "filter_by_item_types",
	FilterByCategory = "filter_by_categories",
	FilterByManufacturers = "filter_by_manufacturers",
	FilterByVendors = "filter_by_vendors",
	FilterBySyncSource = "filter_by_sync_source",
	FilterBySupplierIds = "filter_by_supplier_ids"
}

export class SelfCatalogFilters {
	filter_by_item_types?: ProductType[];
	filter_by_categories?: string[];
	filter_by_manufacturers?: string[];
	filter_by_vendors?: string[];
	filter_by_sync_source?: ZomentumPSA[];
	filter_by_supplier_ids?: string[];
}

// Product search Request contract

export const ETILIZE_PAGE_SIZE = 20;
export const UNIFIED_PAGE_SIZE = 50;

export class ProductSearchRequest {
	q: string | null = null;
	exclude_drop_ship_wh = false;
	filter_by_item_sources: string[] = [];
	cursor: ItemSearchCursor | null = null;
	filter_by_categories: string[] = [];
	filter_by_manufacturers: string[] = [];
	filter_by_features: FeatureFilter[] = [];
	filter_by_connected_distributors = false;
	filter_by_distributors: Distributor[] = [];
	filter_by_custom_distributors: string[] = [];
	exclude_out_of_stock_products = false;
	filter_by_item_types: string[] = [];
	fetch_realtime_pricing = false;
	self_catalog_filters?: SelfCatalogFilters;
	is_request_for_lazy_load?: boolean;
	sort_by?: SortOptions;

	filter_by_sub_categories?: string[] = [];
	amazon_refinements?: AmazonRefinementsFilters;

	public static constructSearchRequest = (
		q: string | null,
		source: ProductSource,
		cursor: ItemSearchCursor | null,
		fetchRealtimePricing: boolean,
		filterByConnectedDistributors?: boolean,
		filterByDistributors?: Distributor[],
		filterByCustomDistributors?: string[],
		excludeOutOfStock?: boolean,
		excludeDropShipWH?: boolean,
		manufacturerFilters?: string[],
		categoriesFilters?: string[],
		featureFilters?: FeatureFilter[],
		itemTypeFilters?: string[],
		selfCatalogFilters?: SelfCatalogFilters,
		is_request_for_lazy_load?: boolean,
		sort_by?: SortOptions,

		amazonSubCategory?: string[],
		amazonRefineMentsFilters?: AmazonRefinementsFilters
	): ProductSearchRequest => {
		const searchRequest = new ProductSearchRequest();
		const sourceFilters = [source.toString()];
		searchRequest.q = q && q.length > 0 ? q : null;
		searchRequest.filter_by_item_sources = sourceFilters;
		searchRequest.cursor = cursor;
		searchRequest.filter_by_manufacturers = manufacturerFilters ?? [];
		searchRequest.filter_by_categories = categoriesFilters ?? [];
		searchRequest.filter_by_features = featureFilters ?? [];
		searchRequest.filter_by_connected_distributors =
			!!filterByConnectedDistributors;
		searchRequest.filter_by_distributors = filterByDistributors ?? [];
		searchRequest.filter_by_custom_distributors =
			filterByCustomDistributors ?? [];
		searchRequest.exclude_out_of_stock_products = !!excludeOutOfStock;
		searchRequest.exclude_drop_ship_wh = !!excludeDropShipWH;
		searchRequest.filter_by_item_types = itemTypeFilters ?? [];
		searchRequest.fetch_realtime_pricing = fetchRealtimePricing;
		searchRequest.self_catalog_filters = selfCatalogFilters;
		searchRequest.is_request_for_lazy_load = is_request_for_lazy_load;
		searchRequest.sort_by = sort_by;

		searchRequest.filter_by_sub_categories = amazonSubCategory ?? [];
		searchRequest.amazon_refinements = amazonRefineMentsFilters;
		return searchRequest;
	};
}

export interface AmazonFilterProps {
	readonly includeOutOfStockProducts?: string[];
	readonly eligibleForFreeShipping?: boolean;
	readonly amazonPrimeEligible?: boolean;
	readonly amazonProductCategory?: string[];
	readonly amazonProductSubCategory?: string[];
	readonly amazonProductDeliveryDay?: string[];
}

export enum AmazonCatalogFilterKeys {
	FilterByProductCategory = "filter_by_product_category",
	FilterBySubCategory = "filter_by_sub_categories",
	FilterByDeliveryDay = "filter_by_delivery_day",
	FilterByAvailability = "filter_by_availability"
}

export class AmazonRefinementsFilters {
	amazon_delivery_date?: string;
	include_out_of_stock_products?: string;
	amazon_prime_eligible?: string;
	eligible_for_free_shipping?: string;
}
export class PricingSearchRequest {
	etilize_products_details: EtilizeProductDetails[] | null = null;
	icecat_products_details?: IcecatProductDetails[] | null = null;
	custom_distributor_products_details?:
		| CustomDistributorProductDetails[]
		| null = null;
	exclude_drop_ship_wh = false;
	q: string | null = null;
	public static constructSearchRequest = (
		etilize_products_details: EtilizeProductDetails[],
		icecat_products_details: IcecatProductDetails[],
		custom_distributor_products_details: CustomDistributorProductDetails[],
		exclude_drop_ship_wh: boolean,
		q: string | null
	): PricingSearchRequest => {
		const searchRequest = new PricingSearchRequest();
		searchRequest.etilize_products_details = etilize_products_details;
		searchRequest.icecat_products_details = icecat_products_details;
		searchRequest.custom_distributor_products_details =
			custom_distributor_products_details;
		searchRequest.exclude_drop_ship_wh = exclude_drop_ship_wh;
		searchRequest.q = q?.length ? q : null;
		return searchRequest;
	};
}

export class ItemSearchCursor {
	current_source: ProductSource = ProductSource.Self;
	current_page_no = 1;
	page_size = 20;
}

export class EtilizeProductDetails {
	id: string | null = null;
	manufacturer_part_no?: string = "";
	etilize_skus_info?: [];
}

export class IcecatProductDetails {
	id: string | null = null;
	manufacturer_part_no?: string = "";
	icecat_skus_info?: [];
}

export class CustomDistributorProductDetails {
	id: string | null = null;
	manufacturer_part_no?: string = "";
}

export class FeatureFilter {
	id = "";
	values: string[] = [];
}
export class AmazonRefineMents {
	availability_options: ProductKeyValuePair[] = [];
	delivery_day_options: ProductKeyValuePair[] = [];
	eligible_for_free_shipping_options: ProductKeyValuePair[] = [];
	prime_eligible: ProductKeyValuePair[] = [];
}

export interface AmazonProductAppliedFilters {
	readonly categories: ProductKeyValuePair[];
	readonly sub_categories: ProductKeyValuePair[];
	readonly availability_options: ProductKeyValuePair[];
}

export class ProductMetaInfo {
	manufacturers: ProductKeyValuePair[];
	categories: ProductKeyValuePair[];
	icecatCategories: ProductKeyValuePair[];
	features: ProductFeature[];
	sub_categories: ProductKeyValuePair[];
	amazon_refinements: AmazonRefineMents;
	constructor() {
		this.manufacturers = new Array<ProductKeyValuePair>();
		this.categories = new Array<ProductKeyValuePair>();
		this.icecatCategories = new Array<ProductKeyValuePair>();
		this.features = new Array<ProductFeature>();
		this.sub_categories = new Array<ProductKeyValuePair>();
		this.amazon_refinements = new AmazonRefineMents();
	}
}

export class ProductSearchResponse {
	base = "";
	filtered_count_map: ProductCount = new ProductCount();
	data: Product[] = new Array<Product>();
	links: Links = new Links();
	next: ItemSearchCursor = new ItemSearchCursor();
	meta_info: ProductMetaInfo = new ProductMetaInfo();
}

export class SelfProductPricingSearchData {
	item_id = "";
	pricing: ProductPricingPlan[] = new Array<ProductPricingPlan>();
}

export class SelfProductPricingSearchResponse {
	data: SelfProductPricingSearchData[] =
		new Array<SelfProductPricingSearchData>();
}

export class ProductKeyValuePair {
	label = "";
	value = "";
	count = 0;
}

export class ProductFeature {
	label = "";
	value = "";
	count = 0;
	children: ProductKeyValuePair[] = new Array<ProductKeyValuePair>();
}

export type commonProductFormKeys =
	| "selected_cost_price"
	| "sell_price"
	| "quantity"
	| "should_add_setup_price_to_margin"
	| "selected_setup_price"
	| "type"
	| "margin"
	| "markup"
	| "show_individual_product_pricing"
	| "retail_price"
	| "supplier_id";

export const getSelectableProductBulkUpdateFields = (
	customFields: CustomField[]
) => {
	return [
		{ display_value: "Title", id_value: "name" },
		{
			display_value: "Description",
			id_value: "description"
		},
		{
			display_value: "Category",
			id_value: "product_category"
		},
		{
			display_value: "Manufacturer",
			id_value: "manufacturer"
		},
		{
			display_value: "Tax Category",
			id_value: "tax_category_id"
		},
		{
			display_value: "UOM",
			id_value: "unit_of_measure"
		}
	].concat([
		...customFields
			?.filter(
				customField =>
					customField.is_value_editable_from_ui !== false &&
					(!customField.system_field_id ||
						customField.system_field_id.length === 0)
			)
			?.map(customField => {
				return {
					display_value: customField.display_name,
					id_value: `custom_fields.${customField.id}`
				};
			})
	]);
};

export enum UOM {
	CASE = "Case",
	EACH = "Each",
	PerHour = "Per Hour",
	PerMonth = "Per Month"
}

export enum BulkUpdateProductFilterDisplayValues {
	TITLE = "Title"
	// Add more filters here - For future reference
}

export class MergeDuplicateProductsRequest {
	primary_item_id = "";
	public static constructMergeDuplicateProductsRequest = (
		primaryProductId: string
	): MergeDuplicateProductsRequest => {
		const mergeDuplicateProductsRequest =
			new MergeDuplicateProductsRequest();
		mergeDuplicateProductsRequest.primary_item_id = primaryProductId;
		return mergeDuplicateProductsRequest;
	};
}

export enum SortOptions {
	CostPriceHighToLow = "cost_price_high_to_low",
	CostPriceLowToHigh = "cost_price_low_to_high",
	SellPriceHighToLow = "sell_price_high_to_low",
	SellPriceLowToHigh = "sell_price_low_to_high",
	NameAToZ = "name_a_to_z",
	NameZToA = "name_z_to_a"
}

export enum ProductMiscEnums {
	DefaultPricing = "Default Pricing"
}

export enum FilterOptions {
	Manufacturers = "Manufacturers",
	Categories = "Categories",
	Features = "Features",
	SelfProductType = "filter_by_item_types",
	SelfCategory = "filter_by_categories",
	SelfManufacturers = "filter_by_manufacturers",
	SelfVendors = "filter_by_vendors",
	SelfSyncSource = "filter_by_sync_source",
	Distributors = "filter_by_distributors"
}

export enum FilterTagType {
	Manufacturer = "Manufacturer",
	Category = "Category",
	Feat = "Feat",
	SelfProductType = "filter_by_item_types",
	SelfCategory = "filter_by_categories",
	SelfManufacturer = "filter_by_manufacturers",
	SelfVendor = "filter_by_vendors",
	SelfSupplier = "filter_by_supplier_ids",
	SelfSyncSource = "filter_by_sync_source",
	CustomDistributor = "CustomDistributor",
	AmazonProductCategory = "filter_by_amazon_product_category",
	AmazonProductSubCategory = "filter_by_amazon_product_sub_category",
	AmazonProductDeliveryDay = "filter_by_amazon_product_deliveryDay",
	AmazonProductAvailability = "filter_by_amazon_product_availabitity"
}
export class FilterTagData {
	type: FilterTagType = FilterTagType.Manufacturer;
	id = "";
	name = "";
	featChildId?: string;
	featChildName?: string;
	isSelfFilter?: boolean;
}

export const SortingOptions: IOptionProps[] = [
	{
		name: "Sell price: Low to High",
		value: SortOptions.SellPriceLowToHigh
	},
	{
		name: "Sell price: High to Low",
		value: SortOptions.SellPriceHighToLow
	},
	{
		name: "Cost price: Low to High",
		value: SortOptions.CostPriceLowToHigh
	},
	{
		name: "Cost price: High to Low",
		value: SortOptions.CostPriceHighToLow
	},
	{
		name: "Name: A to Z",
		value: SortOptions.NameAToZ
	},
	{
		name: "Name: Z to A",
		value: SortOptions.NameZToA
	}
];

export interface BundleSettingDetail {
	readonly showChildItems: boolean;
	readonly showQuantity: boolean;
	readonly showPrice: boolean;
}
